/**
 * One room's services, all of them (FRD §8: "A room can carry multiple services
 * in the same cycle").
 *
 * The grid edits Wash & Iron inline because it is two numbers. This modal is
 * the whole-room view behind the Room cell: what the room currently carries,
 * what each service is worth, and a way to add or remove one - without leaving
 * the grid or losing the operator's place in it.
 *
 * It does not duplicate the item editor. Adding or editing Dry Cleaning / Shoe
 * Care hands off to `ItemLinesModal`, exactly as the design does, so garment
 * pricing has one implementation.
 */
import { Plus, Printer, Save, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { useColors } from '../../../hooks/useColors'
import { Modal, PillButton, SelectInput } from './primitives'
import { formatKg, formatMoney } from '../format'
import { toNumber } from '../draft'
import { B2B_SERVICES } from '../serviceCatalogue'
import { DRY_CLEANING_SLUG, SHOE_CARE_SLUG, WEIGHT_SERVICE_SLUG } from './billingServices'
import { draftWeightAmount, entryItemsAmount } from './billingRowModel'
import type { BillingField } from './BillingRow'
import type { RoomEntry } from './billingRowModel'

export interface RoomServiceModalProps {
  roomNumber: string
  entry: RoomEntry
  /** Amounts already saved for this room, for services the entry has not touched. */
  savedDcAmount: number
  savedShoeAmount: number
  savedDcCount: number
  savedShoeCount: number
  onChangeWeight: (field: BillingField, value: string) => void
  onEditItems: (serviceSlug: string) => void
  onRemoveService: (serviceSlug: string) => void
  onClose: () => void
  onSave: () => void
  /** Tags can only be printed for work that is on the server. */
  canPrint: boolean
  onPrintTags: () => void
}

interface ServiceLine {
  slug: string
  label: string
  active: boolean
  summary: string
  amount: number
}

export default function RoomServiceModal({
  roomNumber,
  entry,
  savedDcAmount,
  savedShoeAmount,
  savedDcCount,
  savedShoeCount,
  onChangeWeight,
  onEditItems,
  onRemoveService,
  onClose,
  onSave,
  canPrint,
  onPrintTags,
}: RoomServiceModalProps) {
  const colors = useColors()
  const [serviceToAdd, setServiceToAdd] = useState(DRY_CLEANING_SLUG)

  const weightAmount = draftWeightAmount(entry.weight)
  const weightActive = Boolean(entry.weight.weightKg.trim())

  const itemSummary = (slug: string, savedCount: number, savedAmount: number): ServiceLine => {
    const lines = entry.items[slug]
    const count = lines ? lines.length : savedCount
    const amount = entryItemsAmount(entry, slug) ?? savedAmount
    const pieces = lines
      ? lines.reduce((sum, line) => sum + Math.max(0, Math.trunc(toNumber(line.quantity))), 0)
      : 0

    return {
      slug,
      label: B2B_SERVICES.find((service) => service.slug === slug)?.label ?? slug,
      active: count > 0,
      summary: count > 0 ? `${count} item${count === 1 ? '' : 's'}${pieces ? ` · ${pieces} pieces` : ''}` : 'Not added',
      amount,
    }
  }

  const serviceLines: ServiceLine[] = [
    {
      slug: WEIGHT_SERVICE_SLUG,
      label: 'Wash & Iron',
      active: weightActive,
      summary: weightActive
        ? `${formatKg(toNumber(entry.weight.weightKg))} kg × ₹${entry.weight.ratePerKg || 0}/kg`
        : 'Not added',
      amount: weightAmount,
    },
    itemSummary(DRY_CLEANING_SLUG, savedDcCount, savedDcAmount),
    itemSummary(SHOE_CARE_SLUG, savedShoeCount, savedShoeAmount),
  ]

  const roomTotal = Number(serviceLines.reduce((sum, line) => sum + line.amount, 0).toFixed(2))

  return (
    <Modal
      title={`Room ${roomNumber}`}
      onClose={onClose}
      width="620px"
      footer={
        <>
          <PillButton
            variant="secondary"
            onClick={onPrintTags}
            disabled={!canPrint}
            title={canPrint ? undefined : 'Save this room before printing its tags'}
          >
            <Printer size={14} /> Print tags
          </PillButton>
          <PillButton onClick={onSave}>
            <Save size={14} /> Save room
          </PillButton>
        </>
      }
    >
      <p className="font-body" style={{ margin: '0 0 14px', fontSize: '12px', color: colors.textSecondary }}>
        Add, edit or remove services without leaving the billing grid.
      </p>

      <div data-testid="service-lines" style={{ borderTop: `1px solid ${colors.border}` }}>
        {serviceLines.map((line) => (
          <div
            key={line.slug}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 96px 32px',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 0',
              borderBottom: `1px solid ${colors.surface}`,
            }}
          >
            <div>
              <div className="font-body" style={{ fontSize: '13px', fontWeight: 700 }}>
                {line.label}
              </div>
              <div
                className="font-body"
                style={{ fontSize: '11px', color: line.active ? colors.textSecondary : colors.textMuted }}
              >
                {line.summary}
              </div>
            </div>

            <div style={{ textAlign: 'right' }}>
              <div className="font-body" style={{ fontSize: '12px', fontWeight: 700 }}>
                {line.amount > 0 ? formatMoney(line.amount) : '-'}
              </div>
              {line.slug === WEIGHT_SERVICE_SLUG ? null : (
                <button
                  type="button"
                  className="font-body"
                  // Two of these can be on screen at once, so "Add" alone is
                  // not a usable name for anyone navigating by voice or screen
                  // reader.
                  aria-label={`${line.active ? 'Edit' : 'Add'} ${line.label} for room ${roomNumber}`}
                  onClick={() => onEditItems(line.slug)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    fontSize: '10px',
                    fontWeight: 700,
                    color: colors.accentText,
                    cursor: 'pointer',
                  }}
                >
                  {line.active ? 'Edit' : 'Add'}
                </button>
              )}
            </div>

            <button
              type="button"
              aria-label={`Remove ${line.label} from room ${roomNumber}`}
              disabled={!line.active}
              onClick={() => onRemoveService(line.slug)}
              style={{
                display: 'grid',
                placeItems: 'center',
                height: '30px',
                width: '28px',
                borderRadius: '6px',
                border: 'none',
                background: 'transparent',
                color: line.active ? colors.textMuted : 'transparent',
                cursor: line.active ? 'pointer' : 'default',
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {/* Wash & Iron is two numbers, so it is edited here rather than behind
          another modal - the same cells the grid shows, for an operator who
          opened the room instead of typing in the row. */}
      {weightActive ? (
        <div
          style={{
            marginTop: '14px',
            padding: '12px 14px',
            borderRadius: '10px',
            background: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
          }}
        >
          <div
            className="font-body"
            style={{
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.9px',
              textTransform: 'uppercase',
              color: colors.textMuted,
              marginBottom: '8px',
            }}
          >
            Wash &amp; Iron · weight-based
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            <WeightField
              label="Weight (kg)"
              value={entry.weight.weightKg}
              ariaLabel={`Room ${roomNumber} weight`}
              onChange={(value) => onChangeWeight('weightKg', value)}
            />
            <WeightField
              label="Rate (₹/kg)"
              value={entry.weight.ratePerKg}
              ariaLabel={`Room ${roomNumber} rate`}
              onChange={(value) => onChangeWeight('ratePerKg', value)}
            />
            <WeightField
              label="Pieces"
              value={entry.weight.pieces}
              ariaLabel={`Room ${roomNumber} pieces`}
              onChange={(value) => onChangeWeight('pieces', value)}
            />
          </div>
        </div>
      ) : null}

      <div style={{ marginTop: '14px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <SelectInput
          value={serviceToAdd}
          aria-label="Service to add"
          onChange={(event) => setServiceToAdd(event.target.value)}
          style={{ maxWidth: '220px' }}
        >
          {B2B_SERVICES.map((service) => (
            <option key={service.slug} value={service.slug}>
              {service.label}
            </option>
          ))}
        </SelectInput>
        <PillButton
          variant="outline"
          onClick={() => {
            // Adding an item-based service goes straight into its editor -
            // an empty service line would be a dead end.
            if (serviceToAdd === WEIGHT_SERVICE_SLUG) onChangeWeight('weightKg', entry.weight.weightKg || '0')
            else onEditItems(serviceToAdd)
          }}
        >
          <Plus size={14} /> Add service
        </PillButton>
      </div>

      <div
        style={{
          marginTop: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderRadius: '9px',
          border: '1px solid rgba(214,185,123,0.45)',
          background: 'rgba(214,185,123,0.14)',
        }}
      >
        <span
          className="font-body"
          style={{
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.9px',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Room total
        </span>
        <span className="font-display" data-testid="room-total" style={{ fontSize: '22px', fontWeight: 700 }}>
          {formatMoney(roomTotal)}
        </span>
      </div>
    </Modal>
  )
}

function WeightField({
  label,
  value,
  ariaLabel,
  onChange,
}: {
  label: string
  value: string
  ariaLabel: string
  onChange: (value: string) => void
}) {
  const colors = useColors()

  return (
    <label style={{ display: 'block' }}>
      <span
        className="font-body"
        style={{ display: 'block', fontSize: '11px', color: colors.textMuted, marginBottom: '4px' }}
      >
        {label}
      </span>
      <input
        value={value}
        aria-label={ariaLabel}
        inputMode="decimal"
        onChange={(event) => onChange(event.target.value)}
        className="font-body"
        style={{
          width: '100%',
          height: '32px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          background: colors.bgCard,
          padding: '0 9px',
          fontSize: '12px',
          fontWeight: 600,
          color: colors.textPrimary,
        }}
      />
    </label>
  )
}
